import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { VdoHttpError } from '../base/vdo-http-error';
import { StatusCodeEnum } from '@core/enums/status-code.enum';
import { LanguageStorageService } from '@core/services/language-storage.service';

const errorMessages: Record<number, string> = {
  [StatusCodeEnum.BAD_REQUEST]: 'Bad request',
  [StatusCodeEnum.UNAUTHORIZED]: 'Unauthorized',
  [StatusCodeEnum.NOT_FOUND]: 'Not found',
  [StatusCodeEnum.INTERNAL_SERVER_ERROR]: 'Internal server error',
  [StatusCodeEnum.SERVICE_UNAVAILABLE]: 'Service unavailable',
  [StatusCodeEnum.GATEWAY_TIMEOUT]: 'Gateway timeout',
};

function extractValidationErrors(problem: Record<string, string[]>): string | null {
  if (!problem) return null;
  const all = Object.values(problem).flat().filter(Boolean);
  if (!all.length) return null;
  return all.join(' | ');
}

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const languageStorage = inject(LanguageStorageService);
  const uiLang = languageStorage.get();
  const headerLang = languageStorage.uiLangToHeader(uiLang);

  const cloneRequest = req.clone({
    headers: req.headers
      .set('accept-language', headerLang)
      .set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone),
  });

  return next(cloneRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = '';
      if (error.error.errors) {
        const validationErrors = extractValidationErrors(error.error.errors);
        errorMessage = validationErrors ?? 'Validation error occurred.';
      } else if (error?.error?.detail) {
        const httpError = new VdoHttpError(error.error);
        errorMessage = httpError.message;
      } else {
        errorMessage =
          errorMessages[error.status] ?? `Error Code: ${error.status}\nMessage: ${error.message}`;
      }

      return throwError(() => new Error(errorMessage, { cause: error }));
    })
  );
};
